import { Button, Divider, Dropdown, Menu, Modal, message } from "antd";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import DefaultInput from "../../components/form/DefaultInput";
import DefaultSelect from "../../components/form/DefaultSelect";
import DefaultPasswordInput from "../../components/form/DefaultPasswordInput";
import DefaultPhoneInput from "../../components/form/DefaultPhoneInput";
import AddressSelect from "../../components/form/AddressSelect";
import PaginationTable from "../../components/Pagination";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-enterprise";
import classes from "../StaffAccounts/index.module.css";
import { ReactComponent as ExportIcon } from "../../assets/arrow-download-small.svg";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import { ReactComponent as RegisterIcon } from "../../assets/add-plus.svg";
import { ReactComponent as ThreeDots } from "../../assets/three-dots.svg";
import useLang from "../../hooks/useLang";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import { useConfigurationNumber } from "../../hooks/useConfiguration";
import { ConfigurationId } from "../../types/Lookups";
import { fetchZonesCached } from "../../lib/cachedLookups";
import api from "../../utils/axios";
import { CONFIG } from "../../config";
import { toast } from "react-toastify";
import { userRoles } from "../../types/enums";
import { usernamePasswordValidate } from "../../types/UsernamePasswordValidate";
import { useMediaQuery } from "@mui/material";

const SubClientAccounts = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);

	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [tableData, setTableData] = useState([]);
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [address, setAddress] = useState("");
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [open, setOpen] = useState<boolean>(false);
	const [identificationNumber, setIdentificationNumber] = useState<
		string | undefined
	>();
	const [zones, setZones] = useState<any>([]);
	const [countryDropDown, setCountryDropDown] = useState<any>([]);
	const [cityDropDown, setCityDropDown] = useState<any>([]);
	const [country, setCountry] = useState<string | undefined>();
	const [city, setCity] = useState<string | undefined>();
	const [countryLabel, setCountryLabel] = useState<string>("");
	const [cityLabel, setCityLabel] = useState<string>("");
	const [latitude, setLatitude] = useState<string>("");
	const [longitude, setLongitude] = useState<string>("");
	const [sectionId, setSectionId] = useState<number>(0);
	const [sections, setSections] = useState<any>([]);
	const [cities, setCities] = useState<any>([]);
	const [codeId, setCodeId] = useState<number>();
	const [editModal, setEditModal] = useState<boolean>(false);
	const [userData, setUserData] = useState<any>([]);
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [blockModalVisible, setBlockModalVisible] = useState(false);
	const personalIdLength = useConfigurationNumber(
		ConfigurationId.PersonalIdLength
	);
	const [error, setError] = useState<boolean>(false);
	const [userIdToBlock, setUserIdToBlock] = useState<any>();
	const [userIdToDelete, setUserIdToDelete] = useState<any>();
	let grid = useRef(null);
	const { strings } = useLang("staffAccounts");

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const totalItems = tableData.length;

	const fetchTableData = async () => {
		try {
			const response = await api.get("User/sub-clients");
			if (response.status === 200) {
				const result = response?.data;
				setTableData(result);
			}
		} catch (error) {
			console.log("Error: " + error);
		}
	};

	const getZones = useCallback(async () => {
		const allZones = await fetchZonesCached();
		if (allZones) {
			const countries = allZones
				.filter((item: any) => item.levelId === 1)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCountryDropDown(countries);
			const filteredCities = allZones
				.filter((item: any) => item.levelId === 2)
				.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
			setCityDropDown(filteredCities);
			const cities = allZones?.filter((item: any) => item.levelId === 2);
			setCities(cities);
			const sections = allZones.filter((item: any) => item.levelId === 3);
			setSections(sections);
		}
		setZones(allZones);
	}, [zones]);

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	// Data loads once on open; the column effect below only relabels on a language switch.
	useEffect(() => {
		fetchTableData();
		getZones();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.code,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				// width: 80,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "firstName",
				valueGetter: nameAndSurnameGetter,
				headerName: strings?.namesurname,
				width: 150,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "phone",
				headerName: strings?.phone,
				// width: 120,
				cellStyle: () => ({
					fontSize: "15px",
					color: "blue",
				}),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "email",
				headerName: strings?.email,
				// width: 50,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "username",
				headerName: strings?.username,
				// width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "country",
				headerName: strings?.countryLabel,
				// width: 70,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "city",
				headerName: strings?.cityLabel,
				// width: 70,
				suppressSizeToFit: isSmallScreen,
			},
			{
				headerName: "",
				field: "options",
				width: 100,
				cellRendererFramework: (params: any) => {
					const userId = params.data.id;
					const menuItems = optionsMenu(userId);
					return (
						<Dropdown
							menu={{ items: menuItems }}
							trigger={["click"]}
						>
							<ThreeDots onClick={(e) => e.preventDefault()} />
						</Dropdown>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [setTableColumns, strings]);

	const optionsMenu = (userId: number) => [
		{
			label: (
				<Menu.Item onClick={() => handleEditUser(userId)}>
					{strings?.edit}
				</Menu.Item>
			),
			key: "item-1",
		},
		{
			label: (
				<Menu.Item onClick={() => blockUserAccount(userId)}>
					{strings?.block}
				</Menu.Item>
			),
			key: "item-2",
		},
	];

	const handlePageChange = (page: any) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};
	const paginatedStaff = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return tableData.slice(startIndex, endIndex);
	}, [currentPage, tableData]);

	const startIndex = (currentPage - 1) * pageSize + 1;
	const endIndex = startIndex + Math.min(pageSize, totalItems - startIndex);

	const nameAndSurnameGetter = (params: any) => {
		const text = params.data.firstName + " " + params.data.lastName;
		return text;
	};
	const selectedColumnIds = [
		"id",
		"firstName",
		"phone",
		"userRole",
		"email",
		"country",
		"city",
	];
	const countryCodeFromId = (id: any): string => {
		if (id === 1) return "xk";
		if (id === 2) return "al";
		if (id === 3) return "mk";
		return "";
	};

	const resetAddressFields = () => {
		setAddress("");
		setLatitude("");
		setLongitude("");
		setSectionId(0);
	};

	const onCountrySelect = (e: any) => {
		setCity("");
		setCityLabel("");
		resetAddressFields();
		const filteredCities = cities
			.filter((item: any) => item.parentId === e)
			.map((item: any) => ({
				value: item.id,
				label: item.description,
			}));
		setCityDropDown(filteredCities);
		setCountry(e);
		setCountryLabel(countryCodeFromId(e));
	};
	const onCitySelect = (value: any) => {
		if (value?.value !== undefined && value?.value !== null) {
			setCity(value.value);
			setCityLabel(value.label ?? "");
		} else {
			setCity("");
			setCityLabel("");
		}
		resetAddressFields();
	};
	const fetchGeoData = useCallback(async (lat: string, lng: string) => {
		try {
			const response = await api.get("External/get-geo-data", {
				params: {
					lng,
					lat,
					category: CONFIG?.zoneMbledhs ?? "",
				},
			});
			if (response.status === 200) {
				const result = response?.data;
				const section = Number(result.geofences[0]?.name);
				setSectionId(isNaN(section) ? 0 : section);
			}
		} catch (err) {
			console.log(err);
		}
	}, []);

	const onAddressChange = (picked: {
		lat: string;
		lng: string;
		label: string;
	}) => {
		setAddress(picked.label ?? "");
		setLatitude(picked.lat ?? "");
		setLongitude(picked.lng ?? "");
		if (picked.lat && picked.lng) {
			fetchGeoData(picked.lat, picked.lng);
		} else {
			setSectionId(0);
		}
	};
	const onExportSelectedId = () => {
		const dataToExport = filteredData.map((item: any) => ({
			...item,
			firstName: `${item.firstName} ${item.lastName}`,
		}));
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const filteredData = useMemo(() => {
		return paginatedStaff.filter((item: any) => {
			return !codeId || item.id.toString()?.includes(codeId.toString());
		});
	}, [codeId, paginatedStaff]);

	const deleteUserAccount = (userId: any) => {
		setDeleteModalVisible(true);
		setUserIdToDelete(userId);
	};

	const handleDeleteUser = async () => {
		try {
			const response = await api.delete("User/delete", {
				params: { id: userIdToDelete },
			});
			if (response.status === 200) {
				toast.success("Llogaria u fshi me sukses!");
				setDeleteModalVisible(false);
				fetchTableData();
			} else {
				toast.error(
					"Fshirja e llogarise deshtoj. Ju lutem provoni perseri me vone!"
				);
			}
		} catch (error) {
			console.error("Error blocking user account:", error);
		}
	};

	const blockUserAccount = (userId: any) => {
		setBlockModalVisible(true);
		setUserIdToBlock(userId);
	};

	const handleBlockUser = async () => {
		try {
			const response = await api.post("User/block-account", null, {
				params: { userId: userIdToBlock },
			});
			if (response.status === 200) {
				toast.success("Llogaria u bllokua me sukses");
				fetchTableData();
			} else {
				toast.error(
					"Bllokimi i llogarise deshtoj.Ju lutem provoni perseri me vone!"
				);
			}
		} catch (error) {
			console.error("Error blocking user account:", error);
		} finally {
			setBlockModalVisible(false);
		}
	};

	const handleConfirmPasswordChange = (e: any) => {
		setConfirmPassword(e.target.value);
		if (password === e.target.value) {
			setError(false);
		}
	};

	const handleBlur = useCallback(() => {
		if (password !== confirmPassword) {
			message.error("Fjalëkalimet nuk përputhen.");
			setError(true);
		} else {
			setError(false);
		}
	}, [confirmPassword, password]);

	const validateUpdatedData = (data: any) => {
		if (!data?.firstName) {
			toast.error("Emri është i detyrueshëm");
			return false;
		}
		if (!data?.lastName) {
			toast.error("Mbiemri është i detyrueshëm");
			return false;
		}
		if (!data?.phone) {
			toast.error("Numri i telefonit është i detyrueshëm");
			return false;
		}
		if (!data?.address) {
			toast.error("Adresa është e detyrueshme");
			return false;
		}
		if (!data?.username) {
			toast.error("Username është i detyrueshëm");
			return false;
		}
		if (!data?.identificationNumber) {
			toast.error("Nr.Leternjoftimit është i detyrueshëm.");
			return false;
		}
		if (data?.identificationNumber?.length !== personalIdLength) {
			toast.error(
				"Numri i leternjoftimit duhet të jetë i gjatë 10 karaktere."
			);
			return false;
		} else {
			return true;
		}
	};

	const cleanStateOnClose = () => {
		setFullName("");
		setPhone("");
		setUsername("");
		setPassword("");
		setConfirmPassword("");
		setIdentificationNumber("");
		setEmail("");
		setZones([]);
		setCountry("");
		setCity("");
		setCountryLabel("");
		setCityLabel("");
		resetAddressFields();
		setOpen(false);
	};

	const handleEditUser = async (userId: number) => {
		try {
			const response = await api.get("User/userbyid", {
				params: { id: userId },
			});
			if (response.status === 200) {
				let responseData = response.data;
				setUserData(responseData);

				const countryZone = zones?.find(
					(z: any) =>
						z.levelId === 1 &&
						z.description === responseData.country
				);
				const cityZone = zones?.find(
					(z: any) =>
						z.levelId === 2 &&
						z.description === responseData.city &&
						(!countryZone || z.parentId === countryZone.id)
				);

				if (countryZone) {
					const filteredCities = cities
						.filter((item: any) => item.parentId === countryZone.id)
						.map((item: any) => ({
							value: item.id,
							label: item.description,
						}));
					setCityDropDown(filteredCities);
					setCountry(countryZone.id);
					setCountryLabel(countryCodeFromId(countryZone.id));
				} else {
					setCountry(undefined);
					setCountryLabel("");
				}

				if (cityZone) {
					setCity(cityZone.id);
					setCityLabel(cityZone.description ?? "");
				} else {
					setCity("");
					setCityLabel(responseData.city ?? "");
				}

				setAddress(responseData.address ?? "");
				setLatitude(responseData.latitude ?? "");
				setLongitude(responseData.longitude ?? "");
				setSectionId(
					typeof responseData.sectionId === "number"
						? responseData.sectionId
						: 0
				);
				setEditModal(true);
			} else {
				toast.error("Failed to fetch user data");
			}
		} catch (error) {
			console.error("Error fetching user data:", error);
			toast.error("Failed to fetch user data");
		} finally {
		}
	};

	const updateUserData = async () => {
		const fullNameArray = userData?.nameAndSurname.split(" ");
		const firstName = fullNameArray[0] || "";
		const lastName = fullNameArray.slice(1).join(" ");

		const existingSection = sections?.some(
			(item: any) => sectionId !== 0 && item.id === sectionId
		);
		if (!existingSection) {
			toast.error(
				"Kjo Zone nuk eshte e regjistruar! Ju lutem kontaktoni Postman"
			);
			return;
		}

		const data = {
			id: userData.id,
			firstName: firstName,
			lastName: lastName,
			email: userData.email,
			address: address,
			latitude: latitude,
			longitude: longitude,
			sectionId: sectionId,
			phone: userData.phone,
			username: userData.username,
			identificationNumber: userData.identificationNumber,
			password: password,
		};
		if (validateUpdatedData(data)) {
			try {
				const response = await api.post("User/update-subclient", data);
				if (response.status === 200) {
					toast.success("Te dhenat u ndryshuan me sukses!");
					setEditModal(false);
					fetchTableData();
					cleanStateOnClose();
				} else {
					toast.error(
						"Nje gabim ndodhi. Ju lutemi provoni përsëri më vonë!"
					);
				}
			} catch (error: any) {
				console.error("Error updating user:", error);
				toast.error(
					"Nje gabim ndodhi. Ju lutemi provoni përsëri më vonë!"
				);
			}
		} else {
			return;
		}
	};

	const validateInsertedData = (data: any) => {
		const {
			firstName,
			lastName,
			phone,
			address,
			username,
			password,
			identificationNumber,
		} = data;
		if (!firstName) {
			toast.error("Emri është i detyrueshëm");
			return false;
		}
		if (!lastName) {
			toast.error("Mbiemri është i detyrueshëm");
			return false;
		}
		if (!phone) {
			toast.error("Numri i telefonit është i detyrueshëm");
			return false;
		}
		if (!address) {
			toast.error("Adresa është e detyrueshme");
			return false;
		}
		if (!username) {
			toast.error("Username është i detyrueshëm");
			return false;
		}
		if (!password) {
			toast.error("Password është i detyrueshëm");
			return false;
		}
		if (!identificationNumber) {
			toast.error("Nr.Leternjoftimit është i detyrueshëm.");
			return false;
		}
		if (identificationNumber.length !== personalIdLength) {
			toast.error(
				"Numri i leternjoftimit duhet të jetë i gjatë 10 karaktere."
			);
			return false;
		}
		return true;
	};

	const insertNewUser = async () => {
		const fullNameArray = fullName.split(" ");
		const firstName = fullNameArray[0] || "";
		const lastName = fullNameArray.slice(1).join(" ");

		const validation = usernamePasswordValidate(username, password);

		if (!validation.valid) {
			toast.error(validation.message);
			return;
		}

		const existingSection = sections?.some(
			(item: any) => sectionId !== 0 && item.id === sectionId
		);
		if (!existingSection) {
			toast.error(
				"Kjo Zone nuk eshte e regjistruar! Ju lutem kontaktoni Postman"
			);
			return;
		}

		const data = {
			firstName: firstName,
			lastName: lastName,
			email: email ? email : null,
			username: username,
			password: password,
			phone: phone,
			address: address,
			latitude: latitude,
			longitude: longitude,
			sectionId: sectionId,
			userRoleId: userRoles.SUBCLIENT,
			identificationNumber: identificationNumber,
		};
		if (validateInsertedData(data)) {
			try {
				const response = await api.post("User/insert-subclients", data);

				if (response.status === 200) {
					await fetchTableData();
					setOpen(false);
					toast.success("Llogaria u krijua me sukses!");
					cleanStateOnClose();
					return;
				} else {
					toast.error(
						"Një gabim ndodhi gjatë krijimit. Ju lutemi provoni përsëri më vonë"
					);
				}
			} catch (error: any) {
				console.error("Error creating account:", error.response.data);
			}
		} else {
			return;
		}
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	return (
		<>
			<div className={classes.mainContainer}>
				<div className={classes.heroContainer}>
					<div className={classes.firstContainer}>
						<h1 className={classes.title}>
							{strings?.subClientTitle}
							<span className={classes.clientsCount}>
								({tableData?.length})
							</span>
						</h1>
						<div className={classes.buttons}>
							<Button
								className={classes.button}
								onClick={onExportSelectedId}
							>
								<ExportIcon />
								{strings?.exportDataButton}
							</Button>
							<Button
								className={classes.button}
								onClick={() => setOpen(true)}
							>
								<RegisterIcon />
								{strings?.secondButton}
							</Button>
						</div>
					</div>
					<div className={classes.inputs}>
						<DefaultInput
							label={strings?.searchLabel}
							className={classes.input}
							placeholder={strings?.searchPlaceholder}
							prefix={<SearchIcon />}
							value={codeId}
							onChange={(e) => {
								setCurrentPage(1);
								setCodeId(e.target.value);
							}}
						/>
						{/* <DefaultSelect
							label={strings?.countryLabel}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.countryPlaceholder}
							options={countryDropDown}
							value={country}
							onChange={onCountrySelect}
							allowClear
						/>
						<DefaultSelect
							label={strings?.cityLabel}
							flexDirection="col"
							alignCenter="start"
							className={classes.input}
							placeholder={strings?.cityPlaceholder}
							options={cityDropDown}
							value={city}
							onChange={onCitySelect}
							allowClear
						/> */}
					</div>
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
							width: "100%",
						}}
					>
						<AgGridReact
							rowData={filteredData}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="single"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							onFirstDataRendered={onFirstDataRendered}
						/>
					</div>
					<div>
						<PaginationTable
							current={currentPage}
							pageSize={pageSize}
							total={totalItems}
							startOrderIndex={startIndex}
							endOrderIndex={endIndex}
							onChange={handlePageChange}
							onShowSizeChange={handlePageSizeChange}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={open}
				title={strings?.registerModalTitle}
				onCancel={cleanStateOnClose}
				width={1000}
				footer={() => (
					<div className="flex items-end justify-end">
						<Button
							type="primary"
							size="large"
							// className="bg-blue-600"
							style={{ backgroundColor: "#4F16C2" }}
							onClick={insertNewUser}
						>
							{strings?.createAccountButton}
						</Button>
					</div>
				)}
			>
				<Divider />
				<div className={classes.mainModalEditContainer}>
					<DefaultInput
						className={classes.input}
						label={strings?.fullNameLabel}
						placeholder={strings?.fullNamePlaceholder}
						value={fullName}
						onChange={(e) => setFullName(e.target.value)}
					/>
					<DefaultInput
						className={classes.input}
						label={strings?.emailLabel}
						placeholder={strings?.emailPlaceholder}
						value={email}
						onChange={(e) => setEmail(e.target.value)}
					/>
					<DefaultPhoneInput
						className={classes.phoneInput}
						label={strings?.phoneLabel}
						placeholder={strings?.phonePlaceholder}
						initialPhoneNumber={phone}
						onPhoneNumberChange={(v) => setPhone(v)}
					/>
					<DefaultSelect
						className={classes.input}
						label={strings?.countryLabel}
						flexDirection="col"
						alignCenter="start"
						placeholder={strings?.countryPlaceholder}
						options={countryDropDown}
						value={country}
						onChange={onCountrySelect}
					/>
					<DefaultSelect
						className={classes.input}
						label={strings?.cityLabel}
						flexDirection="col"
						alignCenter="start"
						placeholder={strings?.cityPlaceholder}
						options={cityDropDown}
						value={
							city ? { value: city, label: cityLabel } : undefined
						}
						onChange={onCitySelect}
						labelInValue={true}
					/>
					<div
						className="flex flex-col items-start gap-1"
						style={{ width: "15.5rem" }}
					>
						<label className="w-42 -mb-1">
							{strings?.addressLabel}
						</label>
						<AddressSelect
							onAddressChange={onAddressChange}
							country={countryLabel}
							selectedAddress={address || undefined}
							city={city}
							disabled={!city}
							placeholder={strings?.addressPlaceholder}
							style={{ width: "100%" }}
						/>
					</div>
					<DefaultInput
						className={classes.input}
						label={strings?.idNumberLabel}
						value={identificationNumber}
						onChange={(e) =>
							setIdentificationNumber(e.target.value)
						}
						placeholder={strings?.idNumberPlaceholder}
					/>
				</div>
				<Divider />
				<div className={classes.mainModalEditContainer}>
					<DefaultInput
						className={classes.input}
						label={strings?.usernameLabel}
						value={username}
						onChange={(e) => setUsername(e.target.value)}
					/>
					<DefaultPasswordInput
						className={classes.input}
						label={strings?.passwordLabel}
						value={password}
						placeholder={strings?.passwordPlaceholder}
						onChange={(e) => setPassword(e.target.value)}
						onBlur={handleBlur}
						allowClear
					/>
					<DefaultInput
						label={strings?.confirmPasswordLabel}
						className={classes.input}
						value={confirmPassword}
						onChange={handleConfirmPasswordChange}
						onBlur={handleBlur}
						placeholder={strings?.passwordPlaceholder}
						status={error ? "error" : ""}
						type="password"
						suffix={
							error ? (
								<CloseCircleTwoTone twoToneColor="#ff4d4f" />
							) : (
								<CheckCircleTwoTone twoToneColor="#52c41a" />
							)
						}
						allowClear
					/>
				</div>
				<Divider />
			</Modal>
			<Modal
				open={editModal}
				title={strings?.editModalTitle}
				onCancel={() => {
					setEditModal(false);
					cleanStateOnClose();
				}}
				onOk={updateUserData}
				width={1000}
				okButtonProps={{ className: "bg-blue-400" }}
			>
				<Divider />
				<div className={classes.mainModalEditContainer}>
					<DefaultInput
						className={classes.input}
						label={strings?.fullNameLabel}
						placeholder={strings?.fullNamePlaceholder}
						value={userData.nameAndSurname}
						onChange={(e) =>
							setUserData({
								...userData,
								nameAndSurname: e.target.value,
							})
						}
					/>
					<DefaultInput
						className={classes.input}
						label={strings?.emailLabel}
						placeholder={strings?.emailPlaceholder}
						value={userData.email}
						onChange={(e) =>
							setUserData({ ...userData, email: e.target.value })
						}
					/>
					<DefaultPhoneInput
						className={classes.phoneInput}
						label={strings?.phoneLabel}
						placeholder={strings?.phonePlaceholder}
						initialPhoneNumber={userData.phone}
						onPhoneNumberChange={(e) =>
							setUserData({ ...userData, phone: e })
						}
					/>
					<DefaultSelect
						className={classes.input}
						label={strings?.countryLabel}
						flexDirection="col"
						alignCenter="start"
						placeholder={strings?.countryPlaceholder}
						options={countryDropDown}
						value={country}
						onChange={onCountrySelect}
					/>
					<DefaultSelect
						className={classes.input}
						label={strings?.cityLabel}
						flexDirection="col"
						alignCenter="start"
						placeholder={strings?.cityPlaceholder}
						options={cityDropDown}
						value={
							city ? { value: city, label: cityLabel } : undefined
						}
						onChange={onCitySelect}
						labelInValue={true}
					/>
					<div
						className="flex flex-col items-start gap-1"
						style={{ width: "15.5rem" }}
					>
						<label className="w-42 -mb-1">
							{strings?.addressLabel}
						</label>
						<AddressSelect
							onAddressChange={onAddressChange}
							country={countryLabel}
							selectedAddress={address || undefined}
							city={city}
							disabled={!city}
							placeholder={strings?.addressPlaceholder}
						/>
					</div>
				</div>
				<Divider />
				<div className={classes.mainModalEditContainer}>
					<DefaultInput
						className={classes.input}
						label={strings?.usernameLabel}
						value={userData.username}
						onChange={(e) =>
							setUserData({
								...userData,
								username: e.target.value,
							})
						}
					/>
					<DefaultPasswordInput
						className={classes.input}
						label={strings?.passwordLabel}
						value={password}
						placeholder={strings?.passwordPlaceholder}
						onChange={(e) => setPassword(e.target.value)}
						onBlur={handleBlur}
						allowClear
					/>
					<DefaultInput
						className={classes.input}
						label={strings?.confirmPasswordLabel}
						value={confirmPassword}
						onChange={handleConfirmPasswordChange}
						onBlur={handleBlur}
						placeholder={strings?.passwordPlaceholder}
						status={error ? "error" : ""}
						type="password"
						suffix={
							error ? (
								<CloseCircleTwoTone twoToneColor="#ff4d4f" />
							) : (
								<CheckCircleTwoTone twoToneColor="#52c41a" />
							)
						}
						allowClear
					/>
				</div>
				<Divider />
			</Modal>
			<Modal
				title={strings?.confirmDeleteTitle}
				open={deleteModalVisible}
				onOk={handleDeleteUser}
				onCancel={() => setDeleteModalVisible(false)}
				okButtonProps={{ className: "bg-blue-400" }}
				okText={strings?.confirmDeleteOkButton}
				cancelText={strings?.confirmDeleteCancelButton}
			>
				<p>{strings?.confirmDeleteText}</p>
			</Modal>
			<Modal
				title={strings?.confirmBlockTitle}
				open={blockModalVisible}
				onOk={handleBlockUser}
				onCancel={() => setBlockModalVisible(false)}
				okButtonProps={{ className: "bg-blue-400" }}
				okText={strings?.confirmBlockOkButton}
				cancelText={strings?.confirmBlockCancelButton}
			>
				<p>{strings?.confirmBlockText}</p>
			</Modal>
		</>
	);
};

export default SubClientAccounts;
